/**
 * The Daily Report table.
 *
 * Extracted so the full report page and the dashboard's 7-day card render the
 * same thing. Two copies of a thirty-column table would drift on the first
 * column anybody added, and the two would then quietly disagree about what a
 * brand did today.
 *
 * The column list is no longer fixed: a channel's Sales, Leads and CVR columns
 * are dropped for a brand that has nothing in them, so the width depends on how
 * many channels the brand actually runs — see visibleColumns.
 *
 * The columns are grouped rather than run as one flat row, because the table is
 * wider than any screen and a reader scrolling sideways loses the plot without a
 * header telling them which family of numbers they are in. Money and counts
 * alternate down the row, so the group bands are what separate them.
 *
 * Everything is one horizontal scroller with a sticky first column and a sticky
 * header, and the totals row is pinned to the bottom — reading a Wednesday
 * halfway across the table is useless if you cannot see which day it is or what
 * it sums to.
 *
 * ON A PHONE it is not a table at all. Forty columns behind a 390px viewport is
 * a sideways scroll of eleven screens, with the sticky date column eating a
 * third of the width — the figures people actually open this report for are
 * three swipes apart. So below `md` each period is a card: the six headline
 * numbers, and the rest a tap away. Same data, same formatting, same row
 * order; see HEADLINE_KEYS for what earns a place on the front of the card.
 */

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Radio } from 'lucide-react'

/**
 * The table, as data.
 *
 * `group` drives the banded header; `align` and `format` drive the cell. Kept
 * declarative so a new column is one entry rather than four edits spread across
 * the header, the body and the totals row — which is how the three drift.
 */
export const COLUMNS = [
  // Repeat charges, split (backend services/rebills): a Rebill bills a
  // subscription that was already billed once; a Plan Change is any other
  // repeat charge — a new plan, a redo at checkout, an add-on.
  { group: 'Orders', key: 'rebill_orders', label: 'Rebills', format: 'count' },
  { group: 'Orders', key: 'plan_change_orders', label: 'Plan Changes', format: 'count' },
  { group: 'Orders', key: 'new_orders', label: 'New Orders', format: 'count' },
  { group: 'Orders', key: 'total_orders', label: 'Total Orders', format: 'count', emphasis: true },
  { group: 'Orders', key: 'sema_orders', label: 'Sema Orders', format: 'count' },
  { group: 'Orders', key: 'tirz_orders', label: 'Tirz Orders', format: 'count' },

  { group: 'Revenue', key: 'rebill_revenue', label: 'Rebill Revenue', format: 'money' },
  { group: 'Revenue', key: 'plan_change_revenue', label: 'Plan Change Revenue', format: 'money' },
  { group: 'Revenue', key: 'new_revenue', label: 'New Order Revenue', format: 'money' },
  { group: 'Revenue', key: 'total_revenue', label: 'Total Revenue', format: 'money', emphasis: true },
  // Dated on the day of the refund, from Manifold; blank for a clinic whose
  // refunds cannot be read. A charge refunded in full within a day is in
  // neither column — it was never counted as a sale.
  { group: 'Revenue', key: 'refund_count', label: 'Refunds', format: 'count' },
  { group: 'Revenue', key: 'refunds', label: 'Refunded', format: 'money' },
  { group: 'Revenue', key: 'revenue_net_of_refunds', label: 'Net Revenue', format: 'money', emphasis: true },

  // SALES — by price book: which checkout took the money. `web` is an absence
  // rather than a value, the ordinary checkout with no book stamped on it.
  { group: 'Sales', key: 'book_reddit_sales', label: 'Reddit', format: 'count', channel: 'reddit' },
  { group: 'Sales', key: 'book_sales_team_sales', label: 'Sales Team', format: 'count', channel: 'sales_team' },
  // Customer.io's win-back link, which sells on the phone room's price book and
  // is told apart by having no rep behind it. See attribution/registers.
  { group: 'Sales', key: 'book_email_sales', label: 'Email', format: 'count', channel: 'email' },
  // The same link clicked from one of the automation's texts, told apart by
  // the click's utm_medium. Always shown for Science.co, zeros included, since
  // its texts only started carrying that tag on 2026-09-29.
  { group: 'Sales', key: 'book_sms_sales', label: 'SMS', format: 'count', channel: 'sms', alwaysFor: ['scienceco'] },
  { group: 'Sales', key: 'book_web_sales', label: 'Web', format: 'count' },
  { group: 'Sales', key: 'total_sales', label: 'Total Sales', format: 'count', emphasis: true },

  // ATTRIBUTED SALES — by source: the attribution engine's first touch, the same
  // credit the Channels report's First Touch model gives.
  // The SAME sales, partitioned a second way, so this band also sums to Total
  // Sales. A Meta-found customer closed by an agent appears once in each band,
  // which is the point: the two together say who found them and who closed.
  { group: 'Attributed Sales', key: 'meta_sales', label: 'Meta', format: 'count', channel: 'meta' },
  { group: 'Attributed Sales', key: 'google_sales', label: 'Google', format: 'count', channel: 'google' },
  { group: 'Attributed Sales', key: 'reddit_sales', label: 'Reddit', format: 'count', channel: 'reddit' },
  // No Sales Team row: the agents close customers, they do not originate them,
  // so a call-center touch lands in Other here and the phone room is counted in
  // the Sales band above. Same rule the Leads band has always used.
  { group: 'Attributed Sales', key: 'other_sales', label: 'Other', format: 'count' },
  // Identical to Total Sales by construction. Shown so the band visibly
  // partitions the same sales rather than asking the reader to trust it.
  { group: 'Attributed Sales', key: 'attributed_sales', label: 'Total Attributed', format: 'count', emphasis: true },

  { group: 'AOV', key: 'rebill_aov', label: 'Rebill AOV', format: 'money' },
  { group: 'AOV', key: 'new_aov', label: 'New Sales AOV', format: 'money' },
  { group: 'AOV', key: 'blended_aov', label: 'Blended AOV', format: 'money', emphasis: true },

  // Leads and their conversion, per channel and then in total. Each pair sits
  // together rather than all the counts followed by all the rates: the question
  // a reader has at a channel is "how many, and did they buy", and separating
  // the two halves by five columns makes that a scroll instead of a glance.
  //
  // No Sales Team pair: the agents close leads rather than originate them, so
  // their work is in Sales and Attributed Sales, not here.
  { group: 'Leads', key: 'reddit_leads', label: 'Reddit Leads', format: 'count', channel: 'reddit' },
  { group: 'Leads', key: 'reddit_cvr', label: 'Reddit CVR', format: 'percent', channel: 'reddit' },
  { group: 'Leads', key: 'meta_leads', label: 'Meta Leads', format: 'count', channel: 'meta' },
  { group: 'Leads', key: 'meta_cvr', label: 'Meta CVR', format: 'percent', channel: 'meta' },
  { group: 'Leads', key: 'google_leads', label: 'Google Leads', format: 'count', channel: 'google' },
  { group: 'Leads', key: 'google_cvr', label: 'Google CVR', format: 'percent', channel: 'google' },
  { group: 'Leads', key: 'other_leads', label: 'Other Leads', format: 'count' },
  { group: 'Leads', key: 'other_cvr', label: 'Other CVR', format: 'percent' },
  { group: 'Leads', key: 'leads', label: 'Total Leads', format: 'count', emphasis: true },
  { group: 'Leads', key: 'lead_cvr', label: 'Lead CVR', format: 'percent', emphasis: true },

  { group: 'Acquisition', key: 'ad_spend', label: 'Total Ad Spend', format: 'money' },
  { group: 'Acquisition', key: 'ad_spend_cac', label: 'Ad Spend CAC', format: 'money' },
  { group: 'Acquisition', key: 'total_cac', label: 'Total CAC', format: 'money', emphasis: true },

  // COSTS — what the period cost and what it returned.
  //
  // Total Revenue, AOV, Ad Spend and Total CAC belong to this question too but
  // already have columns above, so they are not repeated here — cost per
  // acquisition is answered once, by Total CAC in Acquisition.
  //
  // COGS is charged per SHIPMENT, not per charge: a three-month bundle is one
  // charge and three fills. On Science.co 2026-09-15 that was 12 charges and 23
  // fills, so counting charges would have understated the cost by 48%.
  //
  // It is also drug + shipping, which is NOT what the rest of the app reports —
  // LTV, cross-sell and the P&L read drug cost alone, half the landed cost on
  // Science.co. That is a known gap being fixed separately; this section is
  // built on the true number.
  { group: 'Costs', key: 'cogs_new_subs', label: 'COGS (New Subs)', format: 'money' },
  { group: 'Costs', key: 'net_revenue', label: 'Net Rev (less Refunds, COGS & Ad Spend)', format: 'money', emphasis: true },
  // Both are multiples, and they are deliberately adjacent so the difference is
  // visible: ROAS is revenue over AD SPEND, PSM is revenue over TOTAL cost.
  { group: 'Costs', key: 'total_roas', label: 'Total ROAS', format: 'multiple' },
  { group: 'Costs', key: 'psm_1mo', label: 'PSM 1mo (COGS based)', format: 'multiple' },
]

/** Column groups in order, with their widths, for the banded header row. */
function groupsOf(columns) {
  return columns.reduce((acc, col) => {
    const last = acc[acc.length - 1]
    if (last && last.name === col.group) last.span += 1
    else acc.push({ name: col.group, span: 1 })
    return acc
  }, [])
}

/**
 * Drop the channel columns this brand has nothing in.
 *
 * Every channel carried by every brand would be a thirty-one column table, and
 * most brands run two channels: Science.co has no Sales Team and no Google,
 * TMATES has no Reddit and no price book at all. A column of dashes costs a
 * reader the same horizontal scroll as a column of numbers and tells them
 * nothing, so a channel earns its two columns by having a sale or a lead
 * somewhere in the window.
 *
 * Totals are checked as well as the rows: a brand can have activity in a period
 * that is summed into the totals row while every visible row is zero, and
 * hiding a column whose total is non-zero would make the totals row lie.
 *
 * Only `channel` columns are droppable. Nothing here can hide Total Sales or
 * Other — those are the columns that make the row add up. A column with
 * `alwaysFor` stays for those clinics even when it is all zeros.
 */
function visibleColumns(rows, totals, clinic, remembered = []) {
  // While the table is loading or still filling in, the channels it showed last
  // time are kept, so it opens at the width it will settle at instead of
  // gaining columns as the numbers arrive.
  const used = new Set(remembered)
  const clinicId = String(clinic || '').toLowerCase()
  for (const column of COLUMNS) {
    if (!column.channel || used.has(column.channel)) continue
    if (column.alwaysFor?.includes(clinicId)) { used.add(column.channel); continue }
    const hasValue = source => source && Number(source[column.key]) > 0
    if (rows.some(hasValue) || hasValue(totals)) used.add(column.channel)
  }
  return COLUMNS.filter(column => !column.channel || used.has(column.channel))
}

/**
 * What a card leads with on a phone.
 *
 * Chosen as the answer to "how did this day go": what came in, how many
 * bought, what each paid, what it cost to get them. The keys are the same ones
 * the table emphasises, so the two never disagree; the labels are shorter
 * because a card has no group header above it to lend them context.
 */
const HEADLINE_KEYS = [
  ['total_revenue', 'Revenue'],
  ['total_orders', 'Orders'],
  ['total_sales', 'New sales'],
  ['blended_aov', 'AOV'],
  ['ad_spend', 'Ad spend'],
  ['total_cac', 'Total CAC'],
]

/** Alternating tints so a group reads as a block across a wide scroll. */
const GROUP_TINT = {
  Orders: 'bg-muted/30',
  Revenue: '',
  Sales: 'bg-muted/30',
  // Every group needs an entry. A missing one interpolates `undefined` straight
  // into the cell's class list, which is how Attributed Sales and Costs were
  // rendering after they were added.
  'Attributed Sales': '',
  AOV: '',
  Leads: 'bg-muted/30',
  Acquisition: '',
  Costs: 'bg-muted/30',
  // The CFO report's groups (pages/CfoReportPage).
  Declines: 'bg-muted/30',
  'New Patients': '',
  'Recurring Patients': 'bg-muted/30',
}

/**
 * Money is stored in cents everywhere in this codebase, so it is divided here
 * and nowhere else. Null is an undefined ratio — a period with no orders has no
 * AOV, which is not the same as an AOV of zero — and renders as an em dash.
 */
export function formatMoney(cents) {
  if (cents === null || cents === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

export function formatCount(value) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-US').format(value)
}

export function formatPercent(value) {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(1)}%`
}

/**
 * A multiple, e.g. ROAS. Rendered with an × so it is never mistaken for money
 * or for a percentage — 2.4 and $2.40 and 2.4% are three different claims.
 */
export function formatMultiple(value) {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(2)}×`
}

/** A cell whose number has not arrived yet. Same footprint as a figure. */
function Pending() {
  return <span aria-hidden className="inline-block h-3.5 w-10 rounded bg-muted-foreground/15 align-middle animate-pulse" />
}

/**
 * The channel columns this clinic showed last, so the next load can lay the
 * table out at its final width before any number is in. Per browser; a miss
 * just means the first load sizes itself from the data.
 */
const CHANNELS_KEY = clinic => `daily-report-channels:${String(clinic || '').toLowerCase()}`

function rememberedChannels(clinic) {
  try { return JSON.parse(localStorage.getItem(CHANNELS_KEY(clinic)) || '[]') } catch { return [] }
}

function rememberChannels(clinic, columns) {
  try {
    localStorage.setItem(CHANNELS_KEY(clinic), JSON.stringify([...new Set(columns.map(c => c.channel).filter(Boolean))]))
  } catch { /* storage unavailable: the next load sizes from the data */ }
}

function formatCell(column, row) {
  const value = row[column.key]
  if (column.format === 'money') return formatMoney(value)
  if (column.format === 'percent') return formatPercent(value)
  // A 0–1 share, as the CFO report sends its rates.
  if (column.format === 'ratio') return formatPercent(value == null ? value : value * 100)
  if (column.format === 'multiple') return formatMultiple(value)
  return formatCount(value)
}

/**
 * One period as a card: the headline figures, and every other column behind a
 * tap. Rendered below `md` only — see the file comment.
 */
function PeriodCard({ row, columns, columnGroups, isLive, isTotals = false, renderCell = formatCell, loading = false, headlineKeys = HEADLINE_KEYS }) {
  const [open, setOpen] = useState(false)
  const byKey = useMemo(() => new Map(columns.map(column => [column.key, column])), [columns])
  const headlines = headlineKeys.filter(([key]) => byKey.has(key))
  const hiddenCount = columns.length - headlines.length

  return (
    <div
      className={`rounded-lg border ${
        isLive
          ? 'border-sky-500/60 bg-sky-50 dark:bg-sky-950'
          : isTotals ? 'border-border bg-muted' : 'border-border bg-card'
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/60">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          {isTotals ? 'Total' : loading ? <Pending /> : row.label}
          {isLive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:bg-sky-400/15 dark:text-sky-300">
              <Radio className="h-2.5 w-2.5" />
              Live
            </span>
          )}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-3 py-2.5">
        {headlines.map(([key, label]) => (
          <div key={key}>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className={`text-base font-semibold tabular-nums ${row[key] ? '' : 'text-muted-foreground'}`}>
              {renderCell(byKey.get(key), row)}
            </dd>
          </div>
        ))}
      </dl>

      {hiddenCount > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen(value => !value)}
            aria-expanded={open}
            className="flex w-full items-center justify-center gap-1 border-t border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50"
          >
            {open ? 'Hide' : `Show all ${columns.length}`}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="border-t border-border/60 px-3 pb-3">
              {columnGroups.map(group => (
                <div key={group.name} className="pt-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.name}
                  </div>
                  <dl className="mt-1 divide-y divide-border/50">
                    {columns.filter(column => column.group === group.name).map(column => (
                      <div key={column.key} className="flex items-baseline justify-between gap-3 py-1">
                        <dt className="text-xs text-muted-foreground">{column.label}</dt>
                        <dd className={`text-sm tabular-nums ${column.emphasis ? 'font-semibold' : ''} ${row[column.key] ? '' : 'text-muted-foreground'}`}>
                          {renderCell(column, row)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * @param {object[]} rows            period rows, already in display order
 * @param {object}   [totals]        omit to hide the totals row
 * @param {string}   granularity     daily | weekly | monthly
 * @param {string}   [currentPeriodKey]  the period still being written to, from
 *                                   the API — never computed from the browser
 *                                   clock, which is a different day outside ET
 * @param {boolean}  [loading]       no data yet: `rows` are placeholders and
 *                                   every cell shimmers, at the final layout
 * @param {string[]} [pendingKeys]   columns whose values are still coming
 *                                   (the API's `pending_keys`); they shimmer
 */
/**
 * `columnSet` and `headlineKeys` let another report draw its own columns in
 * this same table (the CFO report does); without them it is the Daily Report.
 */
export default function DailyReportTable({ rows, totals, granularity, currentPeriodKey, clinic, loading = false, pendingKeys = [], columnSet = null, headlineKeys = HEADLINE_KEYS }) {
  const firstColumnLabel =
    granularity === 'monthly' ? 'Month' : granularity === 'weekly' ? 'Week' : 'Date'

  const settling = loading || pendingKeys.length > 0
  const columns = useMemo(
    () => columnSet || visibleColumns(rows, totals, clinic, settling ? rememberedChannels(clinic) : []),
    [columnSet, rows, totals, clinic, settling]
  )
  const columnGroups = useMemo(() => groupsOf(columns), [columns])
  // Remembered once the numbers are final, for the next load's layout.
  useEffect(() => {
    if (!columnSet && !settling && rows.length) rememberChannels(clinic, columns)
  }, [columnSet, settling, rows.length, clinic, columns])

  const pending = useMemo(() => new Set(pendingKeys), [pendingKeys])
  const renderCell = (column, row) => (loading || pending.has(column.key) ? <Pending /> : formatCell(column, row))

  return (
    <>
      {/* Phones: a card per period. The table below is hidden rather than
          reflowed — a forty-column grid has no honest narrow layout. */}
      <div className="space-y-3 md:hidden">
        {rows.map(row => (
          <PeriodCard
            key={row.key}
            row={row}
            columns={columns}
            columnGroups={columnGroups}
            isLive={Boolean(currentPeriodKey && row.key === currentPeriodKey)}
            renderCell={renderCell}
            loading={loading}
            headlineKeys={headlineKeys}
          />
        ))}
        {totals && (
          <PeriodCard
            row={totals}
            columns={columns}
            columnGroups={columnGroups}
            isLive={false}
            isTotals
            renderCell={renderCell}
            loading={loading}
            headlineKeys={headlineKeys}
          />
        )}
      </div>

    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-20">
          <tr className="bg-background">
            <th className="sticky left-0 z-30 bg-background text-left px-3 py-1.5 border-b border-border" />
            {columnGroups.map(group => (
              <th
                key={group.name}
                colSpan={group.span}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-l border-border ${GROUP_TINT[group.name]}`}
              >
                {group.name}
              </th>
            ))}
          </tr>
          <tr className="bg-background">
            <th className="sticky left-0 z-30 bg-background text-left text-xs font-medium px-3 py-2 border-b border-border whitespace-nowrap">
              {firstColumnLabel}
            </th>
            {columns.map((col, i) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-right text-xs font-medium whitespace-nowrap border-b border-border ${
                  columns[i - 1]?.group !== col.group ? 'border-l' : ''
                } ${GROUP_TINT[col.group]} ${col.emphasis ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            // The period still being written to. Marked rather than left to
            // look like a finished one that happens to be low: a partial day
            // read as a complete day is the single most misleading thing this
            // table can do, and it is at the top of it.
            const isLive = currentPeriodKey && row.key === currentPeriodKey
            return (
              <tr
                key={row.key}
                // Opaque, and with a dark variant. The tint used to be `bg-sky-50/70`
                // alone: a near-white wash that, in dark mode, sat under light text
                // and made the one row that most needs reading unreadable.
                className={isLive
                  ? 'bg-sky-50 hover:bg-sky-100 dark:bg-sky-950 dark:hover:bg-sky-900 group'
                  : 'hover:bg-muted/50 group'}
              >
                <td
                  className={`sticky left-0 z-10 px-3 py-2 font-medium whitespace-nowrap border-b border-border ${
                    isLive
                      // Must be OPAQUE, not merely tinted. This cell is sticky, so
                      // the columns scroll underneath it — at `/70` they showed
                      // through, in light mode as well as dark. Same colours as
                      // the row so the two still read as one band.
                      ? 'bg-sky-50 group-hover:bg-sky-100 dark:bg-sky-950 dark:group-hover:bg-sky-900 border-l-2 border-l-sky-500'
                      : 'bg-background group-hover:bg-muted/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {loading ? <Pending /> : row.label}
                    {isLive && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:bg-sky-400/15 dark:text-sky-300">
                        <Radio className="h-2.5 w-2.5" />
                        Live
                      </span>
                    )}
                  </span>
                </td>
                {columns.map((col, i) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 text-right tabular-nums whitespace-nowrap border-b border-border ${
                      columns[i - 1]?.group !== col.group ? 'border-l' : ''
                    } ${isLive ? '' : GROUP_TINT[col.group]} ${col.emphasis ? 'font-semibold' : ''} ${
                      row[col.key] ? '' : 'text-muted-foreground'
                    }`}
                  >
                    {renderCell(col, row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
        {totals && (
          <tfoot className="sticky bottom-0 z-20">
            <tr className="bg-muted font-semibold">
              <td className="sticky left-0 z-30 bg-muted px-3 py-2 whitespace-nowrap border-t-2 border-border">
                Total
              </td>
              {columns.map((col, i) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-right tabular-nums whitespace-nowrap border-t-2 border-border ${
                    columns[i - 1]?.group !== col.group ? 'border-l' : ''
                  }`}
                >
                  {renderCell(col, totals)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    </>
  )
}
